import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const ROLE_LABELS = {
  superadmin: "Superadmin",
  admin: "Leadership",
  school_admin: "Leadership",
  district_admin: "Leadership",
  teacher: "Teacher",
  special_services_teacher: "Teacher",
  parent: "Family",
  family: "Family",
  student: "Student",
};

const Sidebar = ({ links, forceReloadOnNavigate = false }: any) => {
  const location = useLocation();
  const { user } = useAuth();

  const primaryRole = user?.role || (Array.isArray(user?.roles) ? user.roles[0] : null);
  const roleLabel = ROLE_LABELS[primaryRole] || "User";
  const displayName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email || "Academic Eye User";
  const initial = displayName.charAt(0).toUpperCase();

  const isActivePath = useCallback((to) => {
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  }, [location.pathname]);

  const getExpandedFromRoute = useCallback(() => {
    const expanded = {};
    links.forEach(link => {
      const hasActiveChild = link.children && link.children.some(child => isActivePath(child.to));
      const isCurrentParentRoute = isActivePath(link.to);
      if (hasActiveChild || isCurrentParentRoute) {
        expanded[link.to] = true;
      }
    });
    return expanded;
  }, [links, isActivePath]);

  const [expandedSections, setExpandedSections] = useState(() => getExpandedFromRoute());

  // Keep expansion synchronized with current route.
  useEffect(() => {
    setExpandedSections(getExpandedFromRoute());
  }, [location.pathname, getExpandedFromRoute]);

  const toggleSection = (to) => {
    const parentLink = links.find(link => link.to === to);
    if (parentLink && Array.isArray(parentLink.children) && parentLink.children.length > 0) {
      // Do not block Link navigation; just toggle the visual section state.
      setExpandedSections((prev) => ({
        ...prev,
        [to]: !prev[to],
      }));
    }
  };

  const handleLinkClick = (event, to) => {
    if (!forceReloadOnNavigate) {
      return;
    }

    if (event) {
      event.preventDefault();
    }

    if (to !== location.pathname && typeof window !== 'undefined') {
      window.location.assign(to);
    }
  };

  return (
    <aside className="ae-sidebar">
      <img src="/academic-eye-logo.png" alt="Academic Eye" className="ae-sidebar-logo" />
      <div className="ae-sidebar-profile">
        <div className="ae-sidebar-role">{roleLabel}</div>
        <div className="ae-sidebar-user-row">
          <div className="ae-sidebar-avatar">{initial}</div>
          <div className="ae-sidebar-user-name" title={displayName}>{displayName}</div>
        </div>
      </div>
      <div className="ae-sidebar-divider" />
      {links.map(({ to, label, children, groupLabel }) => {
        const hasChildren = Array.isArray(children) && children.length > 0;
        const isExpanded = expandedSections[to];
        const isSelfActive = isActivePath(to);
        const hasActiveChild = hasChildren && children.some((c) => isActivePath(c.to));
        
        // Determine the class based on state priority
        let linkClass = "ae-sidebar-link";
        if (isSelfActive) {
          linkClass = "ae-sidebar-link-active";
        } else if (hasActiveChild) {
          linkClass = "ae-sidebar-link-has-active-child";
        } else if (isExpanded) {
          linkClass = "ae-sidebar-link-expanded";
        }
        
        return (
          <div key={to}>
            <Link
              to={to}
              onClick={(event) => {
                toggleSection(to);
                handleLinkClick(event, to);
              }}
              className={linkClass}
            >
              {label}
              {hasChildren && (
                <span style={{ float: 'right', fontSize: '14px', marginLeft: '8px' }}>
                  {isExpanded ? '▼' : '►'}
                </span>
              )}
            </Link>
            {hasChildren && isExpanded && (
              <div className="ae-sidebar-child-wrap">
                {children.map((child) => (
                  <Link
                    key={child.to}
                    to={child.to}
                    onClick={(event) => handleLinkClick(event, child.to)}
                    className={isActivePath(child.to) ? "ae-sidebar-child-link-active" : "ae-sidebar-child-link"}
                  >
                    {child.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </aside>
  );
};

export default Sidebar;
